import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { imports as importsApi } from "@/api/client";
import type { ManualProvider, ParsedImport } from "@/lib/imports";

interface Staged {
  provider: ManualProvider;
  files: string[];
  data: ParsedImport;
}

type Msg = { ok: boolean; text: string };

/**
 * Settings ▸ Connections ▸ "Import performance". LinkedIn / Substack have no
 * usable insights API, so the user uploads their exports here. We parse + map
 * the file(s) in the browser (lazy-loading SheetJS), preview the counts, then
 * POST the normalized snapshots — which fold into Reports automatically.
 */
export function ImportPanel({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [staged, setStaged] = useState<Staged | null>(null);
  const [parsing, setParsing] = useState(false);
  const [msg, setMsg] = useState<Msg | null>(null);
  const linkedinRef = useRef<HTMLInputElement>(null);
  const substackRef = useRef<HTMLInputElement>(null);

  async function handleFiles(provider: ManualProvider, fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setParsing(true);
    setMsg(null);
    try {
      // Lazy import keeps SheetJS out of the main app bundle.
      const { parseImportFile, mergeParsed } = await import("@/lib/imports");
      const files = Array.from(fileList);
      const data = mergeParsed(await Promise.all(files.map((f) => parseImportFile(provider, f))));
      if (data.account.length === 0 && data.posts.length === 0) {
        setStaged(null);
        setMsg({ ok: false, text: "No rows found in that file — is it the right export?" });
      } else {
        setStaged({ provider, files: files.map((f) => f.name), data });
      }
    } catch (e) {
      setMsg({ ok: false, text: "Couldn't read that file: " + (e instanceof Error ? e.message : "parse error") });
    } finally {
      setParsing(false);
      if (linkedinRef.current) linkedinRef.current.value = "";
      if (substackRef.current) substackRef.current.value = "";
    }
  }

  const importMut = useMutation({
    mutationFn: () =>
      importsApi.run(staged!.provider, { account: staged!.data.account, posts: staged!.data.posts }),
    onSuccess: (res) => {
      setMsg({ ok: true, text: `Imported ${res.imported.account_rows} day(s) · ${res.imported.post_rows} post(s).` });
      setStaged(null);
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (e) => setMsg({ ok: false, text: "Import failed: " + (e instanceof Error ? e.message : "error") }),
  });

  if (!canEdit) return null;

  return (
    <div className="rounded-md border border-dashed p-3 space-y-3">
      <div>
        <div className="text-sm font-medium">Import performance</div>
        <p className="text-xs text-muted-foreground">
          LinkedIn and Substack have no insights API — upload their exports and they fold into
          Reports automatically. Re-importing the same range updates in place.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={linkedinRef}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => handleFiles("linkedin", e.target.files)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={parsing || importMut.isPending}
          onClick={() => linkedinRef.current?.click()}
        >
          <FileSpreadsheet size={14} className="mr-1.5" /> Import LinkedIn (.xlsx)
        </Button>

        <input
          ref={substackRef}
          type="file"
          accept=".csv"
          multiple
          hidden
          onChange={(e) => handleFiles("substack", e.target.files)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={parsing || importMut.isPending}
          onClick={() => substackRef.current?.click()}
        >
          <FileText size={14} className="mr-1.5" /> Import Substack (.csv)
        </Button>

        {parsing ? <span className="text-xs text-muted-foreground">Reading…</span> : null}
      </div>

      {msg ? (
        <div className={cnMsg(msg.ok)}>{msg.text}</div>
      ) : null}

      {staged ? (
        <div className="rounded-md border bg-accent/40 p-3 text-sm space-y-2">
          <div>
            <span className="font-medium capitalize">{staged.provider}</span>
            <span className="text-muted-foreground"> · {staged.files.join(", ")}</span>
          </div>
          <div className="text-xs">
            <b>{staged.data.account.length}</b> day row(s) · <b>{staged.data.posts.length}</b> post(s)
            {staged.data.warnings.length ? (
              <span className="text-amber-600"> · {staged.data.warnings.length} skipped</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => importMut.mutate()} disabled={importMut.isPending}>
              <Upload size={14} className="mr-1.5" />
              {importMut.isPending ? "Importing…" : "Confirm import"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setStaged(null)}
              disabled={importMut.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function cnMsg(ok: boolean): string {
  return ok ? "text-xs text-success" : "text-xs text-destructive";
}
